import React, { useEffect, useRef, useState } from "react";
import { Button, IconButton, Typography } from "@thingsw/pitta-design-system";
import { useTranslation } from "react-i18next";
import CloseIcon from "@material-ui/icons/Close";
import ReactCrop, {
  centerCrop,
  makeAspectCrop,
  Crop,
  PixelCrop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { makeStyles, Theme } from "@material-ui/core";
import { LightColors } from "@thingsw/pitta-modules";
import { useDebounceEffect } from "../hooks/useDebounceEffect";
import { canvasPreview } from "../utils/canvasPreview";
import { blobToFile, fileToBase64 } from "../utils/FileTypeChange";

// This is to demonstate how to make and center a % aspect crop
// which is a bit trickier so we use some helper functions.
function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number
) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: "%",
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

const useStyles = makeStyles((theme: Theme) => ({
  cropContainer: {
    margin: "24px 0",
    width: 343,
    maxHeight: 386,
    "& .ReactCrop__drag-handle": {
      border: "none",
      background: "none",
    },
    "& .ord-ne": {
      borderRight: `2px solid ${LightColors.primary["0"]}`,
      borderTop: `2px solid ${LightColors.primary["0"]}`,
      width: 20,
      height: 20,
      right: 16,
      top: 16,
      "&::before": {
        content: "''",
        position: "absolute",
        top: -10 /* 위 여백 */,
        left: 0 /* 왼쪽 여백 */,
        right: -10 /* 오른쪽 여백 */,
        bottom: 0 /* 아래 여백 */,
        background: "transparent" /* 투명 영역 */,
      },
    },
    "& .ord-nw": {
      borderLeft: `2px solid ${LightColors.primary["0"]}`,
      borderTop: `2px solid ${LightColors.primary["0"]}`,
      width: 20,
      height: 20,
      left: 16,
      top: 16,
      "&::before": {
        content: "''",
        position: "absolute",
        top: -10 /* 위 여백 */,
        left: -10 /* 왼쪽 여백 */,
        right: 0 /* 오른쪽 여백 */,
        bottom: 0 /* 아래 여백 */,
        background: "transparent" /* 투명 영역 */,
      },
    },
    "& .ord-se": {
      borderRight: `2px solid ${LightColors.primary["0"]}`,
      borderBottom: `2px solid ${LightColors.primary["0"]}`,
      width: 20,
      height: 20,
      right: 16,
      bottom: 16,
      "&::before": {
        content: "''",
        position: "absolute",
        top: 0 /* 위 여백 */,
        left: 0 /* 왼쪽 여백 */,
        right: -10 /* 오른쪽 여백 */,
        bottom: -10 /* 아래 여백 */,
        background: "transparent" /* 투명 영역 */,
      },
    },
    "& .ord-sw": {
      borderLeft: `2px solid ${LightColors.primary["0"]}`,
      borderBottom: `2px solid ${LightColors.primary["0"]}`,
      width: 20,
      height: 20,
      left: 16,
      bottom: 16,
      "&::before": {
        content: "''",
        position: "absolute",
        top: 0 /* 위 여백 */,
        left: -10 /* 왼쪽 여백 */,
        right: 0 /* 오른쪽 여백 */,
        bottom: -10 /* 아래 여백 */,
        background: "transparent" /* 투명 영역 */,
      },
    },
    "& .ReactCrop__crop-selection": {
      border: `2px solid ${LightColors.primary["0"]}`,
      backgroundImage: "none !important",
    },
  },
}));

const aspect = 4.29;

interface ProfileCropProps {
  profileImage: string;
  onClose: () => void;
  onSave: (croppedImage: { file: File; base: string }) => void;
}

const ProfileCrop = ({ profileImage, onClose, onSave }: ProfileCropProps) => {
  const { t } = useTranslation();
  const imgRef = useRef<HTMLImageElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const classes = useStyles();

  useEffect(() => {
    const handleCropMove = (e: MouseEvent) => {
      const rect = imgRef.current?.getBoundingClientRect();

      if (!rect) {
        return;
      }

      const clickX = e.clientX - rect.left;
      const clickY = e.clientY - rect.top;

      setCrop((prevCrop) => ({
        ...prevCrop!,
        x: Math.max(
          0,
          Math.min(clickX - prevCrop!.width / 2, rect.width - prevCrop!.width)
        ),
        y: Math.max(
          0,
          Math.min(
            clickY - prevCrop!.height / 2,
            rect.height - prevCrop!.height
          )
        ),
      }));
    };

    const currentImgRef = imgRef.current;

    if (currentImgRef) {
      currentImgRef.addEventListener("click", handleCropMove);
    }

    return () => {
      if (currentImgRef) {
        currentImgRef.removeEventListener("click", handleCropMove);
      }
    };
  }, [crop]);

  useDebounceEffect(
    async () => {
      if (
        completedCrop?.width &&
        completedCrop?.height &&
        imgRef.current &&
        previewCanvasRef.current
      ) {
        // We use canvasPreview as it's much faster than imgPreview.
        canvasPreview(
          imgRef.current,
          previewCanvasRef.current,
          completedCrop,
          1,
          0
        );
      }
    },
    100,
    [completedCrop]
  );

  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    const { width, height } = e.currentTarget;
    setCrop(centerAspectCrop(width, height, aspect));
  }

  const handleSave = async () => {
    const image = imgRef.current;
    const previewCanvas = previewCanvasRef.current;
    if (!image || !previewCanvas || !completedCrop) {
      throw new Error("Crop canvas does not exist");
    }

    const targetWidth = 343;
    const targetHeight = 80;

    const offscreen = new OffscreenCanvas(targetWidth, targetHeight);

    const ctx = offscreen.getContext("2d");
    if (!ctx) {
      throw new Error("No 2d context");
    }

    ctx.drawImage(
      previewCanvas,
      0,
      0,
      previewCanvas.width,
      previewCanvas.height,
      0,
      0,
      offscreen.width,
      offscreen.height
    );

    // You might want { type: "image/jpeg", quality: <0 to 1> } to
    // reduce image size
    const blob = await offscreen.convertToBlob({
      type: "image/png",
    });

    const file = blobToFile(blob, "profile-image.png");
    const base = await fileToBase64(file);

    onSave({
      file,
      base,
    });

    onClose();
  };

  return (
    <div
      style={{
        position: "relative",
      }}
    >
      <div
        style={{
          padding: "0 16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="H5">{t("Edit Image")}</Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <ReactCrop
        className={classes.cropContainer}
        crop={crop}
        aspect={aspect}
        keepSelection
        ruleOfThirds
        onChange={(c) => setCrop(c)}
        onComplete={(c) => setCompletedCrop(c)}
      >
        <img
          ref={imgRef}
          src={profileImage}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
          }}
          onLoad={onImageLoad}
          alt="crop-preview"
        />
      </ReactCrop>
      <div
        style={{
          display: "flex",
          justifyContent: "end",
          padding: "0 16px",
        }}
      >
        <Button onClick={handleSave} color="primary">
          {t("OK")}
        </Button>
      </div>
      {!!completedCrop && (
        <>
          <div>
            <canvas
              ref={previewCanvasRef}
              style={{
                border: "1px solid black",
                objectFit: "contain",
                display: "none",
                width: completedCrop.width,
                height: completedCrop.height,
              }}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ProfileCrop;
